import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { normalizeSalonReportPayload } from './e2eMedia';
import { fetchJsonWithRetry } from './fetchWithRetry';
import { logSalonResilience, TIMEOUT_MS } from './salonResilience';

export function isSignedReportAccessToken(token) {
  const raw = String(token || '').trim();
  if (!raw || !raw.includes('.')) return false;
  const parts = raw.split('.');
  if (parts.length !== 2) return false;
  return /^\d+$/.test(parts[0]) && parts[1].length > 0;
}

export function isAppSessionJwt(token) {
  const raw = String(token || '').trim();
  const parts = raw.split('.');
  return parts.length === 3 && parts[0].startsWith('eyJ');
}

function classifyReportQueryToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return 'none';
  if (isSignedReportAccessToken(raw)) return 'signed_share';
  if (isAppSessionJwt(raw)) return 'app_session_jwt';
  return 'unknown';
}

export async function fetchSalonReport(salonReference, options = {}) {
  const ref = String(salonReference || '').trim();
  if (!ref) throw new Error('salonreference is required');

  const base = getApiBaseUrl();
  const rawToken = String(options.accessToken || '').trim();
  const reportToken = isSignedReportAccessToken(rawToken) ? rawToken : '';

  const requestReport = async (tokenValue, attemptLabel = 'primary') => {
    logSalonResilience('report_fetch', {
      salonReference: ref,
      attempt: attemptLabel,
      withToken: Boolean(tokenValue),
      tokenKind: classifyReportQueryToken(tokenValue),
    });
    let requestUrl = `${base}${SALON_API_PREFIX}/report?session_id=${encodeURIComponent(ref)}`;
    if (tokenValue) {
      requestUrl = `${requestUrl}&token=${encodeURIComponent(tokenValue)}`;
    }
    const data = await fetchJsonWithRetry(
      requestUrl,
      { method: 'GET', timeoutMs: TIMEOUT_MS.REPORT, signal: options.signal },
      { label: 'salon-report', onRetry: options.onRetry },
    );
    if (!data.success) {
      const err = new Error(data.error || 'Failed to load report');
      err.status = data.code === 'expired_token' ? 403 : undefined;
      err.apiResponse = data;
      throw err;
    }
    return normalizeSalonReportPayload(data);
  };

  try {
    try {
      return await requestReport(reportToken, 'primary');
    } catch (firstErr) {
      const authFailed = firstErr.status === 401 || firstErr.status === 403
        || firstErr.apiResponse?.code === 'expired_token'
        || firstErr.apiResponse?.code === 'invalid_token';
      if (authFailed && (reportToken || rawToken)) {
        return await requestReport('', 'without_token');
      }
      throw firstErr;
    }
  } catch (err) {
    throw err;
  }
}

export async function fetchCapturedViews(salonReference, options = {}) {
  const ref = String(salonReference || '').trim();
  if (!ref) throw new Error('salonreference is required');

  const base = getApiBaseUrl();
  const appSessionToken = String(options.appSessionToken || '').trim();
  let url = `${base}${SALON_API_PREFIX}/captured-views?session_id=${encodeURIComponent(ref)}`;
  if (appSessionToken) {
    url = `${url}&token=${encodeURIComponent(appSessionToken)}`;
  }

  const data = await fetchJsonWithRetry(
    url,
    { method: 'GET', timeoutMs: TIMEOUT_MS.REPORT, signal: options.signal },
    { label: 'salon-captured-views', maxAttempts: 2 },
  );
  if (!data.success) {
    throw new Error(data.error || 'Failed to load captured views');
  }
  return data;
}

export function resolveSalonReferenceFromParams(params) {
  return String(
    params.get('session_id')
    || params.get('salonreference')
    || params.get('salon_reference')
    || params.get('skinreference')
    || params.get('skin_reference')
    || '',
  ).trim();
}

export function buildSalonResultPagePath(salonReference, accessToken) {
  const ref = encodeURIComponent(String(salonReference || '').trim());
  const path = `/SalonResultPage?session_id=${ref}`;
  const token = String(accessToken || '').trim();
  if (!isSignedReportAccessToken(token)) return path;
  return `${path}&token=${encodeURIComponent(token)}`;
}
